/**
 * Patient Navigator (PN) consultation script — the beats a navigator works live
 * during the free 25-minute consultation a patient self-books from the
 * eligibility flow (Intake 1.1, BAR-2590).
 *
 * Replaces the *scheduled* intro call. That card ran a 15-minute qualifying call
 * for Patient Access; this one runs the consultation itself, so the order
 * changes: rapport and motivation come before anything is explained, and
 * eligibility is confirmed after the explanation so it lands as good news rather
 * than a screening. The inbound intro call is unchanged and keeps its own card.
 *
 * ENG-OWNED, like `patient-access-script.ts`: a typed definition in the repo
 * rather than a FHIR Questionnaire, so wording changes go through a PR. Content
 * is transcribed from
 * `everself-design-system/mockups/intake-11-pn-consultation-card.html`, which is
 * the source of truth for copy.
 *
 * SCRIPT LINES ARE STATIC. `[procedure]`, `[location]`, `[lb]`, `[date]`,
 * `[BMI]`, `[provider name]` and `[today's verified price]` render literally, so
 * the team reads the same sentence on every call. Only chart FIELDS are computed
 * — BMI, the weight-loss estimate, the physicians at the chosen location and
 * the procedure dates on its board.
 *
 * SCRIPT ACTIONS CARRY NO CONTROL. The navigator reads them; only what is worth
 * charting gets an input, and that is a select or a chip — never a checkbox.
 * The typed fields are the fun-facts note and, in the employer section, the
 * family member's legal name: what no option list can hold.
 * A beat therefore ticks off what it captured, not off "the navigator says they
 * read it".
 *
 * ONE GATED QUESTION. The pregnancy question exists only for a patient recorded
 * female at birth (or, with no birth sex recorded or UNK, by gender) and aged 18–50. It is built in or left out — never rendered as a row to
 * skip — which is why this module exports a builder rather than a constant.
 */

import { Patient } from '@medplum/fhirtypes';
import { BenefitAdministrator, Track, TrackLabel } from './constants';
import {
  BARRIER_OPTIONS,
  CARECREDIT_URL,
  EXPLAIN_DUR,
  EXPLAIN_PROC,
  PacBeat,
  PacBeatAnswers,
  PacBeatContext,
  PacWidget,
  PAYMENT_OPTIONS,
  PRIOR_PROCEDURE_OPTIONS,
  PROCEDURE_OPTIONS,
} from './patient-access-script';
import { readBirthSex } from './birth-details';
import { WEIGHT_RELATED_CONDITIONS, WEIGHT_RELATED_CONDITIONS_ID } from './weight-related-conditions';
import { INSURANCE_SOURCE_OPTIONS, NOT_ELIGIBLE_SOURCES } from './insurance-eligibility';

/* -------------------------------------------------------------------------
   Option vocabularies specific to this script
   ------------------------------------------------------------------------- */

export const PN_GLP1_OPTIONS = ['Tried and stopped', 'Still taking', 'Never tried'] as const;

/**
 * One question, not two. Status and willingness to stop land in a single tap;
 * asking a non-smoker whether they would quit is noise.
 */
export const PN_SMOKING_OPTIONS = ['Non-smoker', 'Will stop', 'Won’t stop'] as const;

export const PN_PREGNANCY_OPTIONS = ['Yes', 'No'] as const;

/**
 * The navigator's read of where the patient stands, and the only field the
 * downstream events key off. The first four are rungs of the same ladder —
 * committed (self-pay), committed (benefit verification), leaning yes,
 * lukewarm — so likelihood is not asked separately.
 */
export const PN_OUTCOME_OPTIONS = [
  'Committed, likely payment in 72 hours',
  'Committed, benefit verification next',
  'Likely, still deciding',
  'Lukewarm, will decide soon',
  'Maybe in the future',
  'Not qualified',
  'Not interested',
] as const;
export type PnOutcome = (typeof PN_OUTCOME_OPTIONS)[number];

/** The one outcome that reads as a positive close in the collapsed preview. Fires `Lychee`. */
export const PN_COMMITTED_OUTCOME: PnOutcome = 'Committed, likely payment in 72 hours';

/**
 * Committed, but paying through an employer benefit or insurance (BAR-2924).
 * Deliberately not `Lychee`: coverage cases are slower and less certain, and
 * must not teach Google Ads to bid toward them. Fires a PostHog-only event,
 * `pn_consult_coverage_intent`.
 */
export const PN_BENEFIT_VERIFICATION_OUTCOME: PnOutcome = 'Committed, benefit verification next';

/** Leaning yes but not closed. Fires `Plum`. */
export const PN_LIKELY_OUTCOME: PnOutcome = 'Likely, still deciding';

/**
 * Which outcomes fire an event when the note is saved: `Lychee` on a committed
 * close (`high`), `Plum` on a likely one (`medium`), the PostHog-only
 * `pn_consult_coverage_intent` on a benefit-verification close (`coverage`),
 * nothing on the rest. bariendo-be's `ConsultNoteQualificationService` matches
 * these outcomes by their text — the two repos share no package — so a change
 * here must land there too.
 */
export const PN_OUTCOME_INTENT: Partial<Record<PnOutcome, 'high' | 'medium' | 'coverage'>> = {
  [PN_COMMITTED_OUTCOME]: 'high',
  [PN_BENEFIT_VERIFICATION_OUTCOME]: 'coverage',
  [PN_LIKELY_OUTCOME]: 'medium',
};

export const PN_BARRIER_OPTIONS = [...BARRIER_OPTIONS, 'Timing'] as const;

/**
 * The self-pay routes. The shared vocabulary less "Employer benefit": that route
 * belongs to the employer section (BAR-2918), so offering it here too would
 * record the same answer in two places. A saved note that already holds it still
 * shows it — the select keeps a stored value it no longer offers.
 */
export const PN_PAYMENT_OPTIONS = PAYMENT_OPTIONS.filter((option) => option !== 'Employer benefit');

/** Where the patient heard of Everself — asked in the small talk, charted for marketing. */
export const PN_SOURCE_OPTIONS = [
  'Facebook',
  'Instagram',
  'Google',
  'YouTube',
  'TV',
  'Friend or family',
  'Doctor',
  'Podcast or radio',
  'News article',
  'Other',
] as const;

/** Why they are here, in the navigator's read — recorded in the internal notes, not asked as a list. */
export const PN_MOTIVATION_OPTIONS = [
  'Not wanting to be on GLP-1 long term',
  'Limited weight loss from GLP-1',
  'Alternative to bariatric surgery',
  'Health issues',
  'Upcoming life milestone',
  'Menopausal weight gain',
  'Aesthetics',
] as const;

/**
 * When the patient wants the procedure, for one who will not name a date from
 * the grid. The card records intent and books nothing — the CC team schedules
 * the real date once payment completes.
 */
export const PN_PROCEDURE_TIMING_OPTIONS = [
  'In 4 weeks',
  'In 2–3 months',
  '3–6 months',
  'Not ready to commit yet',
] as const;

/* -------------------------------------------------------------------------
   The employer section (BAR-2918)
   ------------------------------------------------------------------------- */

/** Asked first, on every track: it decides whose employer the box searches, if anyone's. */
export const PN_EMPLOYMENT_OPTIONS = [
  'Full-time',
  'Part-time with benefits',
  'Benefits through a family member',
  'Neither',
] as const;
export type PnEmployment = (typeof PN_EMPLOYMENT_OPTIONS)[number];

const VIA_FAMILY: PnEmployment = 'Benefits through a family member';
const NOT_EMPLOYED: PnEmployment = 'Neither';

/**
 * The v2.0 card's employer questions (BAR-2997): whose job the benefit comes
 * through, then that job's employment, asked apart rather than in the one
 * shipped question. Their own ids, so a shipped-card note keeps its
 * `emp_status` and reads as it was. A note with either answers by these.
 */
export const PN_BENEFITS_THROUGH_OPTIONS = ['Their job', 'A family member’s job'] as const;
export const PN_EMPLOYMENT_STATUS_OPTIONS = ['Full-time', 'Part-time with health benefits', 'Neither'] as const;
export const PN_BENEFITS_THROUGH_ID = 'emp_through';
export const PN_EMPLOYMENT_STATUS_ID = 'emp_employment';
const THROUGH_OWN_JOB = PN_BENEFITS_THROUGH_OPTIONS[0];
const THROUGH_FAMILY = PN_BENEFITS_THROUGH_OPTIONS[1];
/** The employment that carries an employer benefit; "Neither" does not. */
const BENEFITS_EMPLOYMENT: readonly string[] = PN_EMPLOYMENT_STATUS_OPTIONS.slice(0, 2);

export const PN_BOOK_ONCE_APPROVED_OPTIONS = ['Yes', 'Not yet'] as const;

/** Answer ids the employer section writes. The note stores them like every other answer. */
export const PN_EMPLOYMENT_ID = 'emp_status';
export const PN_EMPLOYER_ID = 'emp_employer';
/** The employer check's verdict: a `BenefitAdministrator`, or {@link NO_EMPLOYER_BENEFIT}. Unset until a check succeeds. */
export const PN_EMPLOYER_BENEFIT_ID = 'emp_benefit';
/**
 * A family member's employer, and its verdict: ids of their own, so the
 * patient's employer and a family member's never share an answer. Changing
 * employment across the two leaves the other branch's answers on the draft,
 * out of the summary, and off the Patient.
 */
export const PN_FAMILY_EMPLOYER_ID = 'emp_family_employer';
export const PN_FAMILY_EMPLOYER_BENEFIT_ID = 'emp_family_benefit';
export const PN_FAMILY_MEMBER_NAME_ID = 'emp_family_name';
export const PN_BOOK_ONCE_APPROVED_ID = 'emp_book';
/** What the employer section's next-step button did: {@link VerificationTaskOutcome}. */
export const PN_EMPLOYER_TASK_ID = 'emp_task';

/**
 * What a coverage section's next-step button did: moved the patient to the
 * coverage track and created the task, or created the task alone because they
 * were already on it.
 */
export type VerificationTaskOutcome = 'switched' | 'created';

/**
 * The stored record: the outcome and the track it was for, e.g.
 * `switched:carrum`. The track lets the card tell a done step from one whose
 * target has since changed (a new employer match), which is offered again.
 * v2.0's button (BAR-3002) adds where the stage went: `:financing` once the
 * patient is in Financing and approval, `:financing-pending` when the consult
 * hasn't ended yet, so the stage follows on the next restage.
 */
export type VerificationTaskStage = 'financing' | 'financing-pending';

export function verificationTaskValue(
  outcome: VerificationTaskOutcome,
  track: Track,
  stage?: VerificationTaskStage
): string {
  return stage ? `${outcome}:${track}:${stage}` : `${outcome}:${track}`;
}

export function readVerificationTask(
  value: unknown
): { outcome: VerificationTaskOutcome; track: Track | undefined; stage?: VerificationTaskStage } | undefined {
  if (typeof value !== 'string') return undefined;
  const [outcome, track, stage] = value.split(':');
  if (outcome !== 'switched' && outcome !== 'created') return undefined;
  return {
    outcome,
    track: Object.values(Track).includes(track as Track) ? (track as Track) : undefined,
    ...((stage === 'financing' || stage === 'financing-pending') && { stage }),
  };
}

/** The line a done button reads, and the saved summary records. */
export function describeVerificationTask(value: unknown, trackName: string): string | undefined {
  const record = readVerificationTask(value);
  if (!record) return undefined;
  const moved = record.outcome === 'switched' ? `Moved to ${trackName} track` : undefined;
  if (record.stage === 'financing') {
    return `${moved ? `${moved} and` : 'Moved to'} Financing and approval · verification task created`;
  }
  if (record.stage === 'financing-pending') {
    return [moved, 'Verification task created · Financing and approval once the consult ends']
      .filter(Boolean)
      .join(' · ');
  }
  return moved ? `${moved} · verification task created` : 'Verification task created';
}

/**
 * The verdict stored when the check found no partner benefit. Only ever written
 * from a successful check: a failed one stores nothing, so an outage never reads
 * as "no benefit".
 */
export const NO_EMPLOYER_BENEFIT = 'none';

/** Procedures an employer benefit covers. Anything else grays the section out. */
export const EMPLOYER_SECTION_PROCEDURES: readonly string[] = ['ESG', 'ESG Max'];

export const ADMINISTRATOR_LABELS: Record<BenefitAdministrator, string> = {
  [BenefitAdministrator.CARRUM]: TrackLabel[Track.CARRUM],
  [BenefitAdministrator.TRANSCARENT]: TrackLabel[Track.TRANSCARENT],
};

function answerValue(answers: PacBeatAnswers, id: string): string | undefined {
  const value = answers.values[id];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Why the employer section doesn't apply, or undefined when it does: the procedure must be ESG or ESG Max. */
export function employerSectionDisabledReason(answers: PacBeatAnswers): string | undefined {
  const procedure = answerValue(answers, 'procedure');
  return procedure && EMPLOYER_SECTION_PROCEDURES.includes(procedure) ? undefined : 'ESG only';
}

function isAdministrator(value: string | undefined): value is BenefitAdministrator {
  return value === BenefitAdministrator.CARRUM || value === BenefitAdministrator.TRANSCARENT;
}

/**
 * The section's result line, or undefined while there is none yet — no
 * employment answer, or an employer not checked (or whose check failed).
 * "Neither" is a result on its own: there is no employer to check.
 */
export function describeEmployerBenefit(
  answers: PacBeatAnswers
):
  | { covered: true; administrator: BenefitAdministrator; label: string }
  | { covered: false; label: string }
  | undefined {
  if (asksBenefitsThrough(answers)) return describeV2EmployerBenefit(answers);
  const employment = answerValue(answers, PN_EMPLOYMENT_ID);
  if (!employment) return undefined;
  if (employment === NOT_EMPLOYED) return { covered: false, label: 'No employer benefit' };

  const benefit = answerValue(
    answers,
    employment === VIA_FAMILY ? PN_FAMILY_EMPLOYER_BENEFIT_ID : PN_EMPLOYER_BENEFIT_ID
  );
  if (isAdministrator(benefit)) {
    return { covered: true, administrator: benefit, label: `Employer benefit · ${ADMINISTRATOR_LABELS[benefit]}` };
  }
  if (benefit === NO_EMPLOYER_BENEFIT) return { covered: false, label: 'No employer benefit' };
  return undefined;
}

/** Whether the note answers the v2.0 employer questions rather than the shipped one. */
function asksBenefitsThrough(answers: PacBeatAnswers): boolean {
  return Boolean(answerValue(answers, PN_BENEFITS_THROUGH_ID) || answerValue(answers, PN_EMPLOYMENT_STATUS_ID));
}

/**
 * The v2.0 verdict (BAR-2997): ✓ only for a partner employer AND full-time or
 * part-time with health benefits; otherwise ✗ with the reason. Undefined while
 * the answers can't say yet.
 */
function describeV2EmployerBenefit(
  answers: PacBeatAnswers
):
  | { covered: true; administrator: BenefitAdministrator; label: string }
  | { covered: false; label: string }
  | undefined {
  // Whose job comes first: with it cleared, the employment asked about it is hidden too.
  const through = answerValue(answers, PN_BENEFITS_THROUGH_ID);
  if (!through) return undefined;
  const employment = answerValue(answers, PN_EMPLOYMENT_STATUS_ID);
  if (employment && !BENEFITS_EMPLOYMENT.includes(employment)) {
    return { covered: false, label: 'No employer benefit · no health benefits through a job' };
  }
  // v2.0 asks the employer first, whoever's job it is, in the one Employer row.
  const benefit = answerValue(answers, PN_EMPLOYER_BENEFIT_ID);
  if (benefit === NO_EMPLOYER_BENEFIT) {
    const employer = answerValue(answers, PN_EMPLOYER_ID);
    return { covered: false, label: `No employer benefit · ${employer ?? 'the employer'} isn’t a partner` };
  }
  if (!isAdministrator(benefit) || !employment) return undefined;
  return { covered: true, administrator: benefit, label: `Employer benefit · ${ADMINISTRATOR_LABELS[benefit]}` };
}

/**
 * Where the employer searched for the benefit is answered: v2.0 asks it once,
 * first, whoever's job it is (BAR-2997); the shipped card asks the family
 * member's employer in its own row.
 */
export function employerAnswerIds(answers: PacBeatAnswers): { employerId: string; benefitId: string } {
  const familyRow = !asksBenefitsThrough(answers) && answerValue(answers, PN_EMPLOYMENT_ID) === VIA_FAMILY;
  return familyRow
    ? { employerId: PN_FAMILY_EMPLOYER_ID, benefitId: PN_FAMILY_EMPLOYER_BENEFIT_ID }
    : { employerId: PN_EMPLOYER_ID, benefitId: PN_EMPLOYER_BENEFIT_ID };
}

const hasEmployerToSearch = (answers: PacBeatAnswers): boolean => {
  const employment = answerValue(answers, PN_EMPLOYMENT_ID);
  return Boolean(employment) && employment !== NOT_EMPLOYED;
};
/** Whether the benefit would come through a family member's employer, not the patient's own. */
export const isViaFamily = (answers: PacBeatAnswers): boolean =>
  asksBenefitsThrough(answers)
    ? answerValue(answers, PN_BENEFITS_THROUGH_ID) === THROUGH_FAMILY
    : answerValue(answers, PN_EMPLOYMENT_ID) === VIA_FAMILY;
/** Whether the benefit would come through the patient's own employer. */
export const isOwnEmployer = (answers: PacBeatAnswers): boolean =>
  asksBenefitsThrough(answers)
    ? answerValue(answers, PN_BENEFITS_THROUGH_ID) === THROUGH_OWN_JOB
    : hasEmployerToSearch(answers) && !isViaFamily(answers);
/** Whose job is answered, on the v2.0 card. */
export const hasBenefitsThrough = (answers: PacBeatAnswers): boolean =>
  Boolean(answerValue(answers, PN_BENEFITS_THROUGH_ID));
/** Whether there is a verdict to show, either card. */
export const hasEmployerBenefitResult = (answers: PacBeatAnswers): boolean =>
  describeEmployerBenefit(answers) !== undefined;
export const hasEmploymentAnswer = (answers: PacBeatAnswers): boolean =>
  Boolean(answerValue(answers, PN_EMPLOYMENT_ID));
export const hasPartnerBenefit = (answers: PacBeatAnswers): boolean =>
  describeEmployerBenefit(answers)?.covered === true;
export const hasNoBenefit = (answers: PacBeatAnswers): boolean => describeEmployerBenefit(answers)?.covered === false;

/* -------------------------------------------------------------------------
   The insurance section (BAR-2919)
   ------------------------------------------------------------------------- */

/** The procedures insurance covers: a rule of its own, though today the same as the employer section's. */
export const INSURANCE_SECTION_PROCEDURES: readonly string[] = ['ESG', 'ESG Max'];

/** Insurance needs the patient to be this old. */
export const INSURANCE_MIN_AGE = 22;

/**
 * Where insurance patients have the procedure, and which states each is the
 * nearer one for (the travel prefill only — whether insurance is offered in a
 * state is bariendo-be's `COVERAGE_STATES`, never this list). `market` is
 * the market Location's name (`packages/bots/data/market-locations.json`),
 * what the Intake and billing Location records (BAR-2922). A new site is a
 * new entry.
 */
export const INSURANCE_TRAVEL_LOCATIONS: readonly { label: string; market: string; nearerFor: readonly string[] }[] = [
  { label: 'Charleston, SC', market: 'Charleston', nearerFor: ['SC', 'GA'] },
  // Not IN, though the spec lists it: Indiana is out of the insurance launch.
  { label: 'Detroit, MI', market: 'Detroit', nearerFor: ['MI', 'OH'] },
];
export const NOT_WILLING_TO_TRAVEL = 'Not willing to travel';
export const INSURANCE_TRAVEL_OPTIONS = [
  ...INSURANCE_TRAVEL_LOCATIONS.map((site) => site.label),
  NOT_WILLING_TO_TRAVEL,
];

/** The market a travel answer names, or undefined for "Not willing to travel" or no answer. */
export function insuranceTravelMarket(travel: string | undefined): string | undefined {
  return INSURANCE_TRAVEL_LOCATIONS.find((site) => site.label === travel)?.market;
}

/** The travel option for a market, e.g. "Charleston, SC" for Charleston; undefined for none. */
export function insuranceTravelLabel(market: string | undefined): string | undefined {
  return INSURANCE_TRAVEL_LOCATIONS.find((site) => site.market === market)?.label;
}

/** The travel option nearer to a state, for the prefill. */
export function nearerTravelLocation(state: string | null | undefined): string | undefined {
  if (!state) return undefined;
  return INSURANCE_TRAVEL_LOCATIONS.find((site) => site.nearerFor.includes(state))?.label;
}

export const PN_POLICYHOLDER_OPTIONS = ['Theirs', 'Spouse’s or partner’s', 'Parent’s'] as const;
export type PnPolicyholder = (typeof PN_POLICYHOLDER_OPTIONS)[number];

/**
 * Whose plan, as the Coverage records it (BAR-2998): `Coverage.relationship`
 * is the patient's relationship to the subscriber, so a parent's plan is
 * `child`. Undefined for the patient's own plan or no answer.
 */
export function policyholderRelationship(value: unknown): 'self' | 'spouse' | 'child' | undefined {
  if (value === PN_POLICYHOLDER_OPTIONS[0]) return 'self';
  if (value === PN_POLICYHOLDER_OPTIONS[1]) return 'spouse';
  if (value === PN_POLICYHOLDER_OPTIONS[2]) return 'child';
  return undefined;
}

/** Whose plan is answered at all: the policyholder row shows, to record or to correct the Coverage's. */
export const hasWhosePlan = (answers: PacBeatAnswers): boolean =>
  policyholderRelationship(answers.values[PN_POLICYHOLDER_ID]) !== undefined;

/**
 * The insurer row's last option on the v2.0 card, as on signup's picker. Not a
 * payer: recorded by name with no Organization or payer key.
 */
export const PN_INSURER_NOT_LISTED = 'Mine isn’t listed';

/** Answer ids the insurance section writes. */
export const PN_INSURANCE_TRAVEL_ID = 'ins_travel';
export const PN_INSURANCE_SOURCE_ID = 'ins_source';
export const PN_INSURER_ID = 'ins_insurer';
export const PN_POLICYHOLDER_ID = 'ins_policyholder';
export const PN_INSURANCE_ELIGIBILITY_ID = 'ins_eligibility';
/**
 * Where notes drafted before the card rows moved to the Coverage kept the
 * member ID and group number. Nothing writes them any more; they are only
 * named so `withoutInsuranceCardAnswers` can drop them from an older draft.
 * Only staging drafts carry them (no release shipped the old rows), so these
 * and that function can go once none does.
 */
export const PN_INSURANCE_MEMBER_ID = 'ins_member_id';
export const PN_INSURANCE_GROUP_ID = 'ins_group';
export const PN_INSURANCE_BOOK_ONCE_APPROVED_ID = 'ins_book';
export const PN_INSURANCE_TASK_ID = 'ins_task';
/**
 * The ids the card has already prefilled from signup, kept on the draft so a
 * field the navigator clears stays clear after the card reloads. Not an answer:
 * no widget writes it, and the summary never reads it.
 */
export const PN_INSURANCE_PREFILLED_ID = 'ins_prefilled';

/**
 * Why the insurance section doesn't apply, or undefined when it does. The
 * first failing rule, in the spec's order: ESG or ESG Max, a covered state,
 * age 22+.
 *
 * The state rule reads `context.coverageArea`, looked up live from
 * bariendo-be. It grays the section only on a definite answer: a known state
 * that bariendo-be does not offer insurance in. A failed lookup (the endpoint
 * not deployed yet, a timeout), a state it can't resolve, or no lookup at all
 * (a saved note) skips the rule, so the navigator can still ask. "Not offered"
 * is never shown on a guess.
 */
/** Why insurance doesn't apply while the patient's state is still being read: waiting, not ruled out. */
export const INSURANCE_CHECKING_STATE = 'Checking state…';

export function insuranceSectionDisabledReason(
  answers: PacBeatAnswers,
  patient: Patient | undefined,
  context?: PacBeatContext
): string | undefined {
  const procedure = answerValue(answers, 'procedure');
  if (!procedure || !INSURANCE_SECTION_PROCEDURES.includes(procedure)) return 'ESG only';

  const area = context?.coverageArea;
  if (area?.status === 'loading') return INSURANCE_CHECKING_STATE;
  if (area?.status === 'ready' && area.state && !area.offered) return `Not offered in ${area.state}`;

  const age = ageInYears(patient?.birthDate, new Date());
  if (age === undefined) return 'Age not on file';
  if (age < INSURANCE_MIN_AGE) return `Age ${INSURANCE_MIN_AGE}+ only`;
  return undefined;
}

/** The patient is on self-pay: no track recorded reads as self-pay, as the backend reads it. */
const isSelfPayTrack = (_answers: PacBeatAnswers, context?: PacBeatContext): boolean =>
  Boolean(context?.trackLoaded) && (context?.track === undefined || context.track === Track.D2C);
export const hasPrivateSource = (answers: PacBeatAnswers): boolean => {
  const source = answerValue(answers, PN_INSURANCE_SOURCE_ID);
  return Boolean(source) && !(NOT_ELIGIBLE_SOURCES as readonly string[]).includes(source as string);
};
export const hasInsuranceSource = (answers: PacBeatAnswers): boolean =>
  Boolean(answerValue(answers, PN_INSURANCE_SOURCE_ID));
/** Whether the recorded likely-eligibility verdict is yes (BAR-2920). */
export const meetsInsuranceCriteria = (answers: PacBeatAnswers): boolean =>
  hasInsuranceSource(answers) && answers.values[`${PN_INSURANCE_ELIGIBILITY_ID}Eligible`] === 'yes';
/** Whether the recorded verdict is no. Neither holds while there is not enough to say. */
export const missesInsuranceCriteria = (answers: PacBeatAnswers): boolean =>
  hasInsuranceSource(answers) && answers.values[`${PN_INSURANCE_ELIGIBILITY_ID}Eligible`] === 'no';

/* -------------------------------------------------------------------------
   The pregnancy gate
   ------------------------------------------------------------------------- */

export const PREGNANCY_QUESTION_MIN_AGE = 18;
export const PREGNANCY_QUESTION_MAX_AGE = 50;

/** Whole years between `birthDate` and `today`, or undefined for no usable date. */
export function ageInYears(birthDate: string | undefined, today: Date): number | undefined {
  if (!birthDate) return undefined;
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return undefined;

  let age = today.getUTCFullYear() - born.getUTCFullYear();
  const beforeBirthday =
    today.getUTCMonth() < born.getUTCMonth() ||
    (today.getUTCMonth() === born.getUTCMonth() && today.getUTCDate() < born.getUTCDate());
  if (beforeBirthday) age -= 1;

  return age;
}

/**
 * Whether the card asks about pregnancy: female and aged 18–50.
 *
 * Sex assigned at birth decides when it is recorded as F or M. With none
 * recorded, or `UNK`, it falls back to `Patient.gender`, which is all the chart
 * held before the PN card started confirming birth sex (BAR-2928).
 *
 * Closed rather than open on a missing birth date. The question is a clinical
 * screen, and an unknown age is not a reason to put it to a patient the rule
 * would otherwise exclude — the NP consult that follows payment asks regardless.
 */
export function pregnancyQuestionApplies(
  patient: Pick<Patient, 'gender' | 'birthDate' | 'extension'> | undefined,
  today: Date = new Date()
): boolean {
  if (!patient) return false;
  const birthSex = readBirthSex(patient);
  const female = birthSex === 'F' || birthSex === 'M' ? birthSex === 'F' : patient.gender === 'female';
  if (!female) return false;

  const age = ageInYears(patient.birthDate, today);
  if (age === undefined) return false;

  return age >= PREGNANCY_QUESTION_MIN_AGE && age <= PREGNANCY_QUESTION_MAX_AGE;
}

/* -------------------------------------------------------------------------
   Long-form script copy. Patient-facing register; internal short forms are
   never spoken aloud.
   ------------------------------------------------------------------------- */

const FRAMEWORK =
  'This call will be about 25 minutes. I’d love to spend the next few minutes getting to know you a little better — what your goals are, what you’ve tried in the past, what’s gotten in the way — and then talk through whether we may be able to help. Does that sound okay?';

export const CARE =
  'Even with a much smaller stomach, it’s crucial that you eat and exercise well. So our services come with 12 months of personalized care. That’s a personal dietitian, a nurse practitioner on call 24/7, group support and a smart scale.';

const ELIGIBLE =
  'Based on this, you should be eligible for our procedure. At your starting weight, the average person loses around [lb]. Once you make a payment we’ll order a comprehensive blood panel and get you in with one of our providers. If the provider finds anything that rules the procedure out, you get a full refund — so in that case the blood work costs you nothing.';

const FINANCING = 'You can pay in full, use HSA or FSA funds, or spread it out with monthly financing.';

const CLOSE =
  'So the next step is yours: [next step]. I’ll send that over as soon as we hang up, and I’ll check in on [day] to see how you’re getting on. It was a real pleasure meeting you, [name].';

/* -------------------------------------------------------------------------
   Beats
   ------------------------------------------------------------------------- */

/**
 * What the chart already says, read before the call. Pinned: it opens with the
 * card and stays open while the navigator works the other beats. "Likely",
 * because none of it has been confirmed on the call yet. BMI and age were not
 * asked for, but they decide which coverage routes are open.
 */
const KEY_FACTS: PacBeat = {
  id: 'facts',
  title: 'Key pre-consultation facts',
  pinned: true,
  items: [
    { kind: 'fact', field: 'procedure', label: 'Likely procedure' },
    { kind: 'fact', field: 'location', label: 'Likely location' },
    { kind: 'fact', field: 'track', label: 'Likely track' },
    { kind: 'fact', field: 'bmi', label: 'BMI' },
    { kind: 'fact', field: 'age', label: 'Age' },
  ],
};

/** Script, plus the one thing worth charting from small talk: where they heard of us. */
const RAPPORT: PacBeat = {
  id: 'rapport',
  title: 'Get to know them & build rapport',
  items: [
    {
      kind: 'action',
      label: 'Introduce yourself',
      say: 'Hi [first name], I’m [rep] — I’m a patient navigator here at Everself.',
    },
    { kind: 'action', label: 'Ask a small-talk question' },
    {
      kind: 'group',
      items: [
        { kind: 'script', text: 'How are you doing today?' },
        { kind: 'script', text: 'Where are you reaching out from?' },
        { kind: 'script', text: 'Have you lived there long?' },
      ],
    },
    { kind: 'chips', id: 'source', label: 'How did you hear about us?', options: PN_SOURCE_OPTIONS },
    { kind: 'action', label: 'Set the framework', say: FRAMEWORK },
  ],
};

const MOTIVATION: PacBeat = {
  id: 'motivation',
  title: 'Understand their motivations & obstacles',
  items: [
    // The consultation is always self-booked from the eligibility flow, so the
    // goal is a fact to read back rather than a question to ask.
    { kind: 'prefilledValue', id: 'wn_main', label: 'Goal at signup' },
    {
      kind: 'action',
      label: 'Discuss the status quo',
      say: 'Tell me about what you’re currently doing to lose weight.',
    },
    {
      kind: 'group',
      items: [
        { kind: 'script', text: 'What have you tried in the past?' },
        { kind: 'script', text: 'Have you used weight-loss medications?' },
        { kind: 'script', text: 'What has worked for you before?' },
        { kind: 'script', text: 'Have you been able to lose weight but struggled to keep it off?' },
        { kind: 'chips', id: 'mo_glp', label: 'GLP-1 experience', options: PN_GLP1_OPTIONS },
        { kind: 'select', id: 'prior', label: 'Previous weight-loss procedure', options: PRIOR_PROCEDURE_OPTIONS },
      ],
    },
    {
      kind: 'action',
      label: 'Understand the barriers',
      say: 'What have been the biggest challenges preventing you from reaching your goals?',
    },
    {
      kind: 'action',
      label: 'Discuss their goals',
      say: 'Tell me about your goals. Where would you like to be six months from now?',
    },
    {
      kind: 'group',
      items: [
        { kind: 'script', text: 'If you were able to lose that weight, what would change for you?' },
        { kind: 'script', text: 'What are you most looking forward to?' },
        { kind: 'note', text: 'Listen for the deeper motivation, not just the number on the scale.' },
      ],
    },
    { kind: 'action', label: 'Discuss why now', say: 'What made you decide to start looking for a solution now?' },
    {
      kind: 'group',
      items: [{ kind: 'script', text: 'Is there anything that might prevent you from moving forward?' }],
    },
  ],
};

const SERVICES: PacBeat = {
  id: 'services',
  title: 'Share about our services',
  items: [
    {
      kind: 'select',
      id: 'procedure',
      label: 'Confirm the procedure',
      options: PROCEDURE_OPTIONS,
      say: 'I see you’re interested in [procedure]. Is that right?',
    },
    {
      kind: 'conditionalScript',
      condition: 'If they’ve had weight-loss surgery before —',
      text: 'In your case this would be Everself SIS or Everself TORe, which is made for people who have had a weight-loss surgery already.',
    },
    {
      kind: 'action',
      label: 'The procedure',
      sayByProcedure: EXPLAIN_PROC,
      sayFallback: 'Confirm the procedure first — the explanation names it out loud.',
    },
    {
      kind: 'action',
      label: 'Duration and recovery',
      sayByProcedure: EXPLAIN_DUR,
      sayFallback: 'Confirm the procedure first.',
    },
    { kind: 'action', label: '12 months of Everself care', say: CARE },
    {
      kind: 'group',
      items: [{ kind: 'action', label: 'Check for understanding', say: 'What questions do you have so far?' }],
    },
  ],
};

/**
 * The pregnancy row, when the gate is open. Built once per variant so the two
 * qualification beats keep stable identities across renders.
 */
export const PREGNANCY_QUESTION: PacWidget = {
  kind: 'chips',
  id: 'ql_preg',
  label: 'Planning a pregnancy in the next 12 months',
  options: PN_PREGNANCY_OPTIONS,
  say: 'And are you planning to become pregnant in the next 12 months?',
};

/**
 * Identity rows read off the Patient; an edit writes the profile, not the note.
 */
const CONFIRM_DETAILS: PacWidget = {
  kind: 'group',
  title: 'Confirm their details',
  items: [
    {
      kind: 'profileField',
      field: 'legalName',
      label: 'Legal name',
      say: 'Can you confirm your full legal name, as it appears on your ID or insurance card?',
    },
    {
      kind: 'profileField',
      field: 'preferredName',
      label: 'Preferred name',
      say: 'And what do you like to be called?',
    },
    { kind: 'profileField', field: 'birthDate', label: 'Date of birth', say: 'And your date of birth?' },
    {
      kind: 'profileField',
      field: 'birthSex',
      label: 'Sex assigned at birth',
      say: 'For your medical record, what sex were you assigned at birth?',
    },
  ],
};

function buildQualify(askPregnancy: boolean, confirmDetails: boolean): PacBeat {
  return {
    id: 'qualify',
    title: 'Confirm medical qualifications',
    items: [
      { kind: 'script', text: 'Next, let me confirm a couple of details to make sure you’d be eligible.' },
      ...(confirmDetails ? [CONFIRM_DETAILS] : []),
      {
        kind: 'computed',
        field: 'bmi',
        label: 'Weight, height and BMI',
        say: 'Your weight is [weight] and your height is [height]. Based on that, your BMI is [BMI].',
      },
      // Prefilled from signup where it maps; feeds the insurance eligibility line.
      {
        kind: 'multiChips',
        id: WEIGHT_RELATED_CONDITIONS_ID,
        label: 'Weight-related conditions',
        options: WEIGHT_RELATED_CONDITIONS,
        say: 'Do you have any of these? Diabetes, high blood pressure, high cholesterol, sleep apnea, fatty liver, joint pain, or reflux that medication hasn’t fixed?',
      },
      ...(askPregnancy ? [PREGNANCY_QUESTION] : []),
      {
        kind: 'chips',
        id: 'ql_smoking',
        label: 'Smoking',
        options: PN_SMOKING_OPTIONS,
        say: 'Do you smoke? And if you do, would you be willing to stop before the procedure?',
      },
      { kind: 'computed', field: 'weightLossEstimate', label: 'Expected weight loss' },
      { kind: 'action', label: 'Confirm their eligibility', say: ELIGIBLE },
    ],
  };
}

const QUALIFY_WITH_PREGNANCY = buildQualify(true, true);
const QUALIFY_WITHOUT_PREGNANCY = buildQualify(false, true);
/** A saved note's qualification beat, without the identity rows. */
const SAVED_QUALIFY_WITH_PREGNANCY = buildQualify(true, false);
const SAVED_QUALIFY_WITHOUT_PREGNANCY = buildQualify(false, false);

export const NEXT_STEPS: PacBeat = {
  id: 'next',
  title: 'Confirm self-pay next steps',
  items: [
    {
      kind: 'locationSelect',
      id: 'loc',
      label: 'Confirm location',
      say: 'Your nearest location would be [location], with Dr. [provider name].',
    },
    // Who operates there, read from the site's procedure blocks — never a
    // hand-maintained list.
    { kind: 'computed', field: 'sitePhysicians', label: 'Physicians at this location' },
    { kind: 'action', label: 'Say a few words about the doctor', say: 'This would be with Dr. [provider name].' },
    {
      kind: 'action',
      label: 'Confirm the date',
      say: 'It looks like we have a couple of slots left on [date]. Are you ready to move forward now? Just so you know, we can’t confirm the date until your payment is in — but I can pencil you in for it today.',
    },
    // The next procedure blocks at the chosen location, named by physician.
    // Selecting one records the date the patient agreed to; it books nothing —
    // the CC team schedules once payment completes.
    { kind: 'procedureDateGrid', id: 'procedure_date' },
    // Coarse timing for a patient who will not name a date yet, or a site with
    // no blocks on the board. "Not ready to commit yet" lives here, not on the
    // grid, so the question is asked once.
    { kind: 'chips', id: 'procdate', label: 'If not one of those, when?', options: PN_PROCEDURE_TIMING_OPTIONS },
    { kind: 'select', id: 'pay', label: 'Payment route', options: PN_PAYMENT_OPTIONS, say: FINANCING },
    // The financing partners, one row each. Cherry's row carries its account
    // status and the send link; the other two are third-party sites.
    {
      kind: 'group',
      title: 'Financing',
      items: [
        { kind: 'computed', field: 'cherryAccount', label: 'Cherry' },
        { kind: 'actionLink', label: 'PatientFi', linkText: 'Open PatientFi', action: 'openPatientFi' },
        {
          kind: 'actionLink',
          label: 'CareCredit',
          linkText: CARECREDIT_URL.replace(/^https:\/\//, ''),
          action: 'openCareCredit',
        },
      ],
    },
    { kind: 'action', label: 'Close with a clear next step', say: CLOSE },
  ],
};

/**
 * Section 5: does an employer cover the procedure? Replaces the
 * everself.com/coverage link, which opened a page and recorded nothing: the
 * search here answers on the call, and the saved note keeps what it found.
 *
 * Grayed out unless the procedure is ESG or ESG Max. The rest is gated on the
 * answers: whose employer to search follows from employment, and the benefit
 * lines only follow a partner match. On the Employer track the box shows the
 * employer already on file, so only employment is asked.
 */
const EMPLOYER_COVERAGE: PacBeat = {
  id: 'employer',
  title: 'Confirm employer coverage benefits',
  disabledReason: employerSectionDisabledReason,
  items: [
    {
      kind: 'chips',
      id: PN_EMPLOYMENT_ID,
      label: 'Employment',
      options: PN_EMPLOYMENT_OPTIONS,
    },
    {
      kind: 'group',
      when: isOwnEmployer,
      items: [
        { kind: 'script', text: 'Who’s your employer?' },
        { kind: 'employerSearch', id: PN_EMPLOYER_ID, resultId: PN_EMPLOYER_BENEFIT_ID, label: 'Employer' },
      ],
    },
    {
      kind: 'group',
      when: isViaFamily,
      items: [
        { kind: 'script', text: 'Where does your family member work, and what’s their full legal name?' },
        {
          kind: 'employerSearch',
          id: PN_FAMILY_EMPLOYER_ID,
          resultId: PN_FAMILY_EMPLOYER_BENEFIT_ID,
          label: 'Employer',
        },
        { kind: 'text', id: PN_FAMILY_MEMBER_NAME_ID, label: 'Family member’s legal name' },
      ],
    },
    {
      kind: 'group',
      when: hasEmploymentAnswer,
      items: [{ kind: 'computed', field: 'employerBenefit', label: 'Result' }],
    },
    {
      kind: 'group',
      when: hasNoBenefit,
      items: [{ kind: 'note', text: 'Insurance or self-pay is the route from here.' }],
    },
    {
      kind: 'group',
      when: hasPartnerBenefit,
      items: [
        { kind: 'script', text: 'Employers usually cover 95–100% of the cost.' },
        {
          kind: 'script',
          text: 'You may need to fly to another Everself location. Your employer usually covers travel and accommodations.',
        },
        {
          kind: 'script',
          text: 'We’ll check with your employer, which usually takes 2 business days, and we’ll be in touch once it’s confirmed.',
        },
        {
          kind: 'chips',
          id: PN_BOOK_ONCE_APPROVED_ID,
          label: 'Book once approved?',
          options: PN_BOOK_ONCE_APPROVED_OPTIONS,
        },
        // "Move to Employer track and create verification task", or "Create
        // verification task" on the Employer track (BAR-2921).
        { kind: 'verificationTask', id: PN_EMPLOYER_TASK_ID, coverage: 'employer' },
      ],
    },
  ],
};

/**
 * Section 6: would their insurance likely cover it? Grayed out unless ESG or
 * ESG Max, in a state bariendo-be offers insurance in, and 22+. How they get
 * insurance, the insurer and whose plan are prefilled from signup whatever the
 * track; the navigator confirms or changes them. Ends in the computed
 * likely-eligibility line.
 *
 * The member ID and group number are insurance card rows, read from and saved
 * to the Coverage, never answers.
 */
const INSURANCE_COVERAGE: PacBeat = {
  id: 'insurance',
  title: 'Confirm insurance coverage benefits',
  disabledReason: insuranceSectionDisabledReason,
  items: [
    {
      kind: 'chips',
      id: PN_INSURANCE_TRAVEL_ID,
      label: 'Travel to an insurance location',
      options: INSURANCE_TRAVEL_OPTIONS,
    },
    {
      kind: 'group',
      when: isSelfPayTrack,
      items: [
        {
          kind: 'script',
          text: 'We now accept most private insurance in your state. It can take up to two months to hear back. Would you like me to check?',
        },
      ],
    },
    {
      kind: 'chips',
      id: PN_INSURANCE_SOURCE_ID,
      label: 'How do they get insurance?',
      options: INSURANCE_SOURCE_OPTIONS,
    },
    {
      kind: 'group',
      when: hasPrivateSource,
      items: [
        { kind: 'insurerSelect', id: PN_INSURER_ID, label: 'Insurance company' },
        { kind: 'chips', id: PN_POLICYHOLDER_ID, label: 'Whose plan', options: PN_POLICYHOLDER_OPTIONS },
      ],
    },
    {
      kind: 'group',
      when: hasInsuranceSource,
      items: [{ kind: 'insuranceEligibility', id: PN_INSURANCE_ELIGIBILITY_ID, label: 'Likely eligibility' }],
    },
    // Talking points by result (BAR-2920): one set for meets criteria, one
    // self-pay line for everything else, out of network included.
    {
      kind: 'group',
      when: meetsInsuranceCriteria,
      items: [
        {
          kind: 'script',
          text: 'We’ll submit a prior authorization to your insurance. It usually takes 1–2 months to hear back.',
        },
        { kind: 'script', text: 'Or you can self-pay with financing and have the procedure in 2–3 weeks.' },
        {
          kind: 'script',
          text: 'A care coordinator will set up 2–3 virtual visits with our dietitian and nurse practitioner to build your case.',
        },
        {
          kind: 'script',
          text: 'If you have your insurance card handy, I can take the details now, or you can send them later.',
        },
        { kind: 'insuranceCardField', field: 'memberId', label: 'Member ID (optional)' },
        { kind: 'insuranceCardField', field: 'groupNumber', label: 'Group number (optional)' },
        {
          kind: 'chips',
          id: PN_INSURANCE_BOOK_ONCE_APPROVED_ID,
          label: 'Book once approved?',
          options: PN_BOOK_ONCE_APPROVED_OPTIONS,
        },
        // "Move to Insurance track and create verification task", or "Create
        // verification task" on the Insurance track (BAR-2922).
        { kind: 'verificationTask', id: PN_INSURANCE_TASK_ID, coverage: 'insurance' },
      ],
    },
    {
      kind: 'group',
      when: missesInsuranceCriteria,
      items: [
        { kind: 'script', text: 'Self-pay with financing is the way to go for now, and I can walk you through it.' },
      ],
    },
  ],
};

/** The widgets without the insurance card rows, at any depth. */
function withoutInsuranceCardRows(items: readonly PacWidget[]): PacWidget[] {
  return items
    .filter((widget) => widget.kind !== 'insuranceCardField')
    .map((widget) => (widget.kind === 'group' ? { ...widget, items: withoutInsuranceCardRows(widget.items) } : widget));
}

/**
 * A saved note's insurance beat, without the insurance card rows, as its
 * qualification beat is without the identity rows: they show today's
 * Coverage, not what the call recorded.
 */
const SAVED_INSURANCE_COVERAGE: PacBeat = {
  ...INSURANCE_COVERAGE,
  items: withoutInsuranceCardRows(INSURANCE_COVERAGE.items),
};

/** Internal — nothing here is said on the call. */
const INTERNAL_NOTES: PacBeat = {
  id: 'notes',
  title: 'Internal notes',
  items: [
    { kind: 'select', id: 'close', label: 'Outcome', options: PN_OUTCOME_OPTIONS },
    { kind: 'multiChips', id: 'motivation', label: 'Motivation', options: PN_MOTIVATION_OPTIONS },
    { kind: 'multiChips', id: 'barrier', label: 'Potential barrier', options: PN_BARRIER_OPTIONS },
    { kind: 'text', id: 'fun_facts', label: 'Fun facts to remember them by' },
  ],
};

export interface PnConsultationScriptContext {
  /** From `pregnancyQuestionApplies`. Decides whether the qualification beat carries the question. */
  askPregnancy: boolean;
  /**
   * Whether the pinned key facts lead the script. Only while the note is being
   * written: a saved note is a record of the call, and facts read from today's
   * chart would misstate what was known going in. Defaults to true.
   */
  keyFacts?: boolean;
  /**
   * Whether the qualification beat leads with the identity rows. Only while the
   * note is being written, for the same reason as the key facts: the rows show
   * today's chart, not what was confirmed on the call. Defaults to true.
   */
  confirmDetails?: boolean;
  /**
   * Whether the insurance beat carries the member ID and group number rows.
   * Only while the note is being written: the rows show today's Coverage, not
   * what the call recorded, and the note never stores them. Defaults to true.
   */
  insuranceCard?: boolean;
}

/**
 * The consultation script for one patient.
 *
 * The pinned key facts, then eight beats in the order the call actually runs:
 *   rapport → motivations → services → qualifications → employer → insurance → self-pay → notes
 *
 * Nothing is explained until the navigator knows what the patient is trying to
 * change, and eligibility is confirmed *after* the explanation. Only the
 * qualification beat varies, and only by the pregnancy row; every other beat is
 * the same object for every patient. The employer beat grays out by its own
 * rule rather than being left out.
 */
export function buildPnConsultationScript({
  askPregnancy,
  keyFacts = true,
  confirmDetails = true,
  insuranceCard = true,
}: PnConsultationScriptContext): readonly PacBeat[] {
  return [
    ...(keyFacts ? [KEY_FACTS] : []),
    RAPPORT,
    MOTIVATION,
    SERVICES,
    confirmDetails
      ? askPregnancy
        ? QUALIFY_WITH_PREGNANCY
        : QUALIFY_WITHOUT_PREGNANCY
      : askPregnancy
        ? SAVED_QUALIFY_WITH_PREGNANCY
        : SAVED_QUALIFY_WITHOUT_PREGNANCY,
    EMPLOYER_COVERAGE,
    insuranceCard ? INSURANCE_COVERAGE : SAVED_INSURANCE_COVERAGE,
    NEXT_STEPS,
    INTERNAL_NOTES,
  ];
}

/** Beat ids, in order — what the collapsed-summary switch is keyed against. */
export const PN_CONSULTATION_BEAT_IDS = buildPnConsultationScript({ askPregnancy: false }).map((beat) => beat.id);
